import React, { useMemo } from 'react';

import { View } from 'react-native';

import { PLAYER_CHANNEL_NAME } from 'config/testID';

import { VideoItem } from 'services/data-manager/types';

import useImageRatio from 'utils/hooks/useImageRatio';
import { isWeb } from 'utils/responsiveness';

import NagraImage from 'components/NagraImage';
import {
    AspectRatio,
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';
import { getImageUrl } from 'components/NagraImage/utils';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

type Props = {
    videoItem: VideoItem | undefined;
    isEmbeddedTabletPlayer: boolean;
    isEmbeddedWebPlayer: boolean;
};

function TopChannelInfo({
    videoItem,
    isEmbeddedTabletPlayer,
    isEmbeddedWebPlayer,
}: Props) {
    const { height } = ImageSize.PLAYER_CHANNEL_LOGO;
    const originalLogoWidth = ImageSize.CHANNEL_LOGO.width * 2;

    const channelLogoUrl = useMemo(() => {
        if (videoItem?.channelId) {
            return getImageUrl({
                aspect: AspectRatio.LANDSCAPE,
                contentId: videoItem.channelId,
                contentType: ImageContentType.CHANNEL,
                imageType: ImageType.LOGO,
                width: originalLogoWidth,
            });
        }

        return undefined;
    }, [videoItem?.channelId]);

    const { ratio: aspectRatio, imageRatioError } =
        useImageRatio(channelLogoUrl);

    if (!videoItem) {
        return null;
    }

    const { channelId, channelTitle, channelNumber } = videoItem;
    const showChannelNumber = channelNumber != null;
    const showChannelLogo = !!(channelLogoUrl && !imageRatioError);
    const showChannelTitle = !!(imageRatioError && channelTitle);
    const shouldRenderBlock =
        showChannelNumber || showChannelLogo || showChannelTitle;

    if (!shouldRenderBlock) {
        return null;
    }

    return (
        <View
            style={[
                styles.root,
                isEmbeddedTabletPlayer && styles.tabletEmbeddedChannelInfoBlock,
                isEmbeddedWebPlayer && styles.webEmbeddedChannelInfoBlock,
            ]}
            accessible={false}
            importantForAccessibility="no-hide-descendants"
            {...(isWeb && { tabIndex: -1 })}
        >
            {showChannelNumber && (
                <Text category={ETextCategory.body2} color={ETextColor.neutral}>
                    {channelNumber}
                </Text>
            )}
            {showChannelLogo && (
                <NagraImage
                    testID={PLAYER_CHANNEL_NAME}
                    style={[
                        styles.channelLogo,
                        !!aspectRatio && {
                            aspectRatio,
                        },
                        { height },
                    ]}
                    resizeMode="contain"
                    contentId={channelId || ''}
                    imageType={ImageType.LOGO}
                    contentType={ImageContentType.CHANNEL}
                    aspect={AspectRatio.LANDSCAPE}
                    width={originalLogoWidth}
                />
            )}
            {showChannelTitle && (
                <Text
                    style={styles.channelTitle}
                    color={ETextColor.neutral}
                    category={ETextCategory.body2}
                    numberOfLines={1}
                    bold
                >
                    {channelTitle}
                </Text>
            )}
        </View>
    );
}

export default React.memo(TopChannelInfo);
